import React, { useState, useRef, forwardRef, useImperativeHandle, useEffect } from 'react';
import { useI18n } from '@/contexts/I18nContext';
import { format } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { Training } from '@/types/trainings';
import { useTrainingsWithNotes } from '@/hooks/useTrainings';
import { formatDateTimeForDisplay } from '@/utils/timezoneUtils';

export interface ClassCardRef {
  triggerStatusUpdateAnimation: () => void;
  animateUpdate: () => void;
  scrollIntoView: () => void;
}

interface ClassCardProps {
  classData: Training;
  onViewTraining: (classData: Training) => void;
  onStatusUpdate: (trainingId: string, status: string) => Promise<void>;
  onNotesUpdate: (classId: string) => void;
  isCurrentDay: boolean;
  notesData: Record<string, any>;
  notesRefreshKey?: number;
  isUpdatingNotes?: boolean;
}

export const ClassCard = forwardRef<ClassCardRef, ClassCardProps>(({
  classData,
  onViewTraining,
  onStatusUpdate,
  onNotesUpdate,
  isCurrentDay,
  notesData,
  notesRefreshKey,
  isUpdatingNotes
}, ref) => {
  const [updateAnimation, setUpdateAnimation] = useState(false);
  const { t } = useI18n();

  // Get localized class type
  const getLocalizedClassType = (type: string) => {
    switch (type) {
      case 'Group Class':
        return t('calendar.group_class');
      case 'Private Class':
        return t('calendar.private_class');
      default:
        return type;
    }
  };

  // Get training notes data from props
  const trainingNotesData = notesData[classData.id];
  const hasNotes = !!(
    trainingNotesData?.notes || // If notes record exists (even with empty fields)
    (trainingNotesData?.techniques && trainingNotesData.techniques.length > 0) ||
    (trainingNotesData?.partners && trainingNotesData.partners.length > 0)
  );



  // Force re-render when notesRefreshKey changes to update notes status
  useEffect(() => {
    // This effect will trigger a re-render when notesRefreshKey changes
    // The hasNotes calculation will be re-evaluated with fresh data
  }, [notesRefreshKey, trainingNotesData, hasNotes]);

  // Determine if we should show action buttons (only for past classes that are still scheduled)
  const classStartTime = new Date(classData.start_time);
  const now = new Date();
  const showActionButtons = classStartTime < now && classData.status === 'Scheduled';

  // Determine if we should show notes button (only for Completed or Missed status)
  const showNotesButton = classData.status === 'Completed' || classData.status === 'Missed';

  // Get card background color (same as TrainingCard)
  const getCardBackgroundColor = (status: string) => {
    switch (status) {
      case 'Scheduled':
        return 'bg-[#524545]';
      case 'Completed':
        return 'bg-[#475245]';
      case 'Missed':
        return 'bg-[#a94442]'; // vermelho escuro para {t('calendar.missed')}
      default:
        return 'bg-[#524545]';
    }
  };

  // Get status color for badge
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Completed':
        return 'bg-green-600 text-white border-green-600';
      case 'Missed':
        return 'bg-red-600 text-white border-red-600';
      case 'Scheduled':
        return 'bg-blue-600 text-white border-blue-600';
      default:
        return 'bg-gray-600 text-white border-gray-600';
    }
  };

  // Format time using central utility
  const formatTime = (timeString: string) => {
    try {
      const { time } = formatDateTimeForDisplay(timeString);
      return time;
    } catch (error) {
      console.error('Error formatting time:', timeString, error);
      return 'Invalid time';
    }
  };

  // Handle status update
  const handleStatusUpdate = async (status: string) => {
    try {
      await onStatusUpdate(classData.id, status);
      triggerStatusUpdateAnimation();
    } catch (error) {
      console.error('Error updating class status:', error);
    }
  };

  // Handle notes click
  const handleNotesClick = () => {
    onNotesUpdate(classData.id);
  };

  // Handle notes save
  const handleNotesSave = () => {
    // This will be handled by the parent component
  };

  // Handle data saved
  const handleDataSaved = () => {
    // Call onNotesUpdate to trigger parent component data refresh
    onNotesUpdate(classData.id);
  };

  // Trigger animation
  const triggerStatusUpdateAnimation = () => {
    setUpdateAnimation(true);
    setTimeout(() => setUpdateAnimation(false), 300);
  };

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    triggerStatusUpdateAnimation,
    animateUpdate: triggerStatusUpdateAnimation,
    scrollIntoView: () => {
      // Scroll the card into view
      const element = document.getElementById(`class-card-${classData.id}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }));

  return (
    <>
      <div 
        id={`class-card-${classData.id}`}
        className={`p-3 rounded-2xl ${getCardBackgroundColor(classData.status)} mb-3 relative transition-all duration-500 ease-out cursor-pointer hover:scale-[1.02] ${
          updateAnimation ? 'scale-105' : ''
        }`}
        onClick={() => onViewTraining(classData)}
      >
        {/* Header with time and type */}
        {/* Class details */}
        <div className="flex items-start gap-3">
          <div className="flex-1 pr-8">
            <div className="flex items-baseline gap-2 mb-2">
              <h3 className="font-bold text-white text-sm font-roboto">
                {classData.title || getLocalizedClassType(classData.type)}
              </h3>
              {classData.modality && (
                <span className="text-white/50 text-[11px] font-medium font-roboto">
                  {classData.modality}
                </span>
              )}
            </div>
            <div className="flex items-center gap-4 text-xs text-white/60 mb-2 font-roboto font-normal">
              <span>{t('calendar.from_to', { start: formatTime(classData.start_time), end: formatTime(classData.end_time) })}</span>
            </div>
            {/* Removed description section as requested */}
          </div>
        </div>

        {/* Status Badge and Notes Button */}
        <div className="flex items-center justify-end mb-3">
          {/* Notes Button - Show for recent {t('calendar.completed')}/{t('calendar.missed')} classes or after status update */}
          {showNotesButton && (
            <>
              {trainingNotesData === null ? (
                // Shimmer loading
                <div className="animate-pulse">
                  <div className="h-4 w-20 bg-white/20 rounded"></div>
                </div>
              ) : (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleNotesClick();
                  }}
                  disabled={isUpdatingNotes}
                  style={{
                    opacity: isUpdatingNotes ? 0.6 : 1,
                    cursor: isUpdatingNotes ? 'not-allowed' : 'pointer'
                  }}
                  className="text-white/80 font-roboto text-xs font-bold underline hover:text-white transition-colors"
                >
                  {hasNotes ? t('calendar.review_notes') : t('calendar.add_notes')}
                </button>
              )}
            </>
          )}
        </div>

        {/* Action Buttons */}
        {showActionButtons && (
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <span className="text-white/80 font-roboto text-xs font-bold">
                {t('calendar.did_class_take_place')}
              </span>
              <div className="flex gap-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleStatusUpdate('Completed');
                  }}
                  className="bg-green-600 hover:bg-green-700 text-white rounded-full font-roboto text-xs font-bold px-4 py-0.5 h-6 leading-none transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('calendar.yes')}
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleStatusUpdate('Missed');
                  }}
                  className="bg-red-600 hover:bg-red-700 text-white rounded-full font-roboto text-xs font-bold px-4 py-0.5 h-6 leading-none transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('calendar.no')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

    </>
  );
});

ClassCard.displayName = 'ClassCard';
